<!--
	Screen-flow, like a tab-group, is a container which allows dynamic content to be defined
		and consumed directly within the html without data structures/js.

	This component works in tandem with the <screen-flow-step> component.
-->
<template>
	<!--<v-stepper class="screen-flow" v-model="currentStepID" :alt-labels="true">-->
	<v-stepper class="screen-flow" v-model="currentStepIndex" :alt-labels="true">
		<!-- dynamically build the nodes from the list of items provided -->
		<v-stepper-header>
			<template v-for="(step, index) in steps">
				<v-stepper-step
					class="screen-flow-node"
					:class="{
						complete: currentStepIndex > index,
						active: currentStepIndex == index,
						'v-stepper__step--active': currentStepIndex == index,
					}"
					:key="step.titleID"
					:step="index"
				>
					<div class="screen-flow-node-circle">
						<v-icon v-if="currentStepIndex > index"
							>mdi-check</v-icon
						>
						<v-icon v-if="currentStepIndex == index"
							>mdi-play</v-icon
						>
					</div>
					{{ step.title }}
				</v-stepper-step>
				<div
					class="screen-flow-node-bar"
					:class="{ complete: index < currentStepIndex }"
					:key="index"
					v-if="index < stepCount"
				></div>
			</template>
		</v-stepper-header>

		<v-stepper-items>
			<slot></slot>
			<div class="screen-flow-action-bar">
				<div v-if="showFlowActionBack" class="screen-flow-action-back">
					<v-btn text @click="checkPrevStep" v-show="currentStepIndex > 0">{{ prevText }}</v-btn>
				</div>
				<div class="additional-actions">
					<slot name="additional-actions">
						<!--
							Define in the calling file
							use 'ref' to access local methods / data for flow steps
						-->
					</slot>
				</div>
				<div v-if="showFlowActionForward" class="screen-flow-action-forward">
					<v-btn class="primary" @click="checkNextStep" :loading="loadingNext">
						{{ nextText }}
					</v-btn>
				</div>
			</div>
		</v-stepper-items>
		<modal-dialog
			v-if="showNextDialog"
			:title="nextDialog.title"
			:executeText="nextDialog.executeText"
			:cancelText="nextDialog.cancelText"
			:executeAction="nextStep.bind()"
			:triggerCounter="nextDialogCounter"
			:useActivator="false"
		>
			<div slot="content">
				<component :is="nextDialog.content"></component>
			</div>
		</modal-dialog>
		<modal-dialog
			v-if="showPrevDialog"
			:title="prevDialog.title"
			:executeText="prevDialog.executeText"
			:cancelText="prevDialog.cancelText"
			:executeAction="prevStep.bind()"
			:triggerCounter="prevDialogCounter"
			:useActivator="false"
		>
			<div slot="content">
				<component :is="prevDialog.content" />
			</div>
		</modal-dialog>
	</v-stepper>
</template>

<script>
	export default {
		name: "screenFlow",
		props: {
			value: {
				required: false,
			},
		},
		created() {
			this.steps = this.$children;
		},
		mounted() {
			// takes a bit of digging, but you'll find the content items if you try real hard
			this.steps = this.steps[0].$children.filter(function (child) {
				return child.$el.className === "screen-flow-content";
			});
			// start from the route
			let startStep = this.$route.params.step ? this.$route.params.step : 0;
			this.selectStep(startStep);
		},
		data() {
			return {
				steps: [],
				currentStepID: "",
				currentStepIndex: 0,
				currentStep: {},
				validate: null,
				nextText: "Next",
				prevText: "Back",
				nextAction: null,
				prevAction: null,
				nextDialog: null,
				prevDialog: null,
				nextDialogCounter: 0,
				prevDialogCounter: 0,
				loadingNext: false,
			};
		},
		computed: {
			stepCount() {
				return this.steps.length - 1;
			},
			showFlowActionForward() {
				let value = true;
				if (this.currentStepID !== "") {
					value = this.currentStep.showFlowActionForward;
				}
				return value;
			},
			showFlowActionBack() {
				let value = true;
				if (this.currentStepID !== "") {
					value = this.currentStep.showFlowActionBack;
				}
				return value;
			},
			showNextDialog() {
				let value = false;
				if (this.currentStepID !== "") {
					value = this.currentStep.showNextDialog;
				}
				return value;
			},
			showPrevDialog() {
				let value = false;
				if (this.currentStepID !== "") {
					value = this.currentStep.showPrevDialog;
				}
				return value;
			},
		},
		methods: {
			selectStep: function (titleIdOrIndex) {
				let convertString = typeof titleIdOrIndex === "string";
				
				// reset isActive on all items
				for (const [index, step] of this.steps.entries()) {
					step.isActive = false;

					// convert a title name into the right index value
					if (convertString && step.titleID === titleIdOrIndex) {
						titleIdOrIndex = index;
					}
				}

				let step = this.steps[titleIdOrIndex];

				step.isActive = true;
				this.currentStep = step;
				this.currentStepID = step.title;
				this.currentStepIndex = titleIdOrIndex;
				this.nextText = step.nextText;
				this.prevText = step.prevText;
				this.nextAction = step.nextAction;
				this.prevAction = step.prevAction;
				this.nextDialog = step.nextDialog;
				this.prevDialog = step.prevDialog;
				this.validate = step.validate;
			},
			getStepIdByIndex(index) {
				return this.steps[index].titleID;
			},
			checkPrevStep() {
				if (this.showPrevDialog) {
					this.prevDialogCounter += 1;
				} else {
					this.prevStep();
				}
			},
			checkNextStep() {
				this.loadingNext = true;
				if (this.showNextDialog) {
					this.nextDialogCounter += 1;
				} else {
					this.nextStep();
				}
			},
			prevStep() {
				// if there's a custom action to take on backward movement,let's run it
				if (this.prevAction) {
					this.prevAction();
				}
				if (this.currentStepIndex > 0) {
					this.currentStepIndex -= 1;
				}
			},
			nextStep() {
				// confirm page validation first and foremost with a promise
				this.loadingNext = true;
				this.validate()
					.then(() => {
						// if there's a custom step to take on forward movement, let's run it
						if (this.nextAction) {
							this.nextAction();
						}
						this.loadingNext = false;
						// move the flow forward
						if (this.currentStepIndex < this.stepCount) {
							this.currentStepIndex += 1;
						}
					})
					.catch((err) => {
						this.loadingNext = false;
						// report validation issues
						const notification = {
							status: "error",
							message: err,
						};
						this.$store.dispatch(
							"notifications/addNotification",
							notification
						);
					});
			},
		},
		watch: {
			// communicate changes via v-bind
			currentStepIndex() {
				this.selectStep(this.currentStepIndex);
				this.$emit("input", this.currentStepID);
			},
			// react to upstream changes via v-bind
			value() {
				var val = this.value.toLowerCase().replace(/ /g, "-");
				this.selectStep(val);
				this.$emit("input", this.currentStepIndex);
			},
		},
	};
</script>

<style lang="scss">
	@import "@/qux-common/design/components/_screen-flow.scss";
</style>
